/**
 * Состояние сделки и её позиции словом человека — одно место на весь проект.
 *
 * <p><b>Почему отдельным файлом.</b> Копий этого словаря было четыре, и они
 * разошлись: «Клиенты» не знали `RETURNED` и показывали в колонке состояния
 * сырой код, экран продавца не знал `DRAFT` у позиции и писал «draft» рядом
 * с деталью необеспеченного заказа. Разошлись они не в момент копирования,
 * а позже — когда правили одну из них. Та же порода, что у таблицы месяцев
 * (`shortDate`) и у сторожа размонтирования (`useMounted`): правило, которое
 * пишут руками во второй раз, во второй раз пишут неправильно.
 *
 * <p><b>Почему словарей всё-таки три, а не один.</b> Они отвечают разным
 * людям и в разных грамматических местах: кабинет говорит о сделке
 * («Отложена») и о позиции («отложена»), а страница по ссылке говорит
 * покупателю о его заказе безлично («отложено»). Свести их в один набор слов
 * значило бы переписать то, что видит покупатель, — а это решение владельца
 * продукта, не исполнителя. Свести надо было **места**, и они здесь: один
 * файл, один тест, один список состояний.
 *
 * <p><b>Полноту держит перебор, а не внимательность.</b> Проверяет её
 * `DealStatusVocabularyTest` на стороне сервера — там перечисление доступно
 * как `values()`, а не как разбор текста, и новое состояние попадает
 * в перебор в тот же момент, когда его дописали в enum. Тест валится
 * и называет недостающее состояние по имени: иначе оно доехало бы до экрана
 * сырым кодом, как уже дважды доезжало. Рядом, в `dealStatus.test.ts`, стоит
 * второй перебор — по экранам, чтобы копия не завелась снова.
 */

/** Все состояния сделки словом. Подлежащее — «сделка», отсюда женский род. */
export const DEAL_STATUS_NAMES: Record<string, string> = {
  DRAFT: 'Черновик',
  RESERVED: 'Отложена',
  READY: 'Готова к выдаче',
  ISSUED: 'Выдана',
  CANCELLED: 'Отменена',
  RETURNED: 'Возвращена',
};

/**
 * Состояния позиции сделки. Подлежащее — «позиция», строчные: слово стоит
 * в строке товара после количества, а не в начале предложения.
 *
 * <p>`DRAFT` здесь — «без резерва»: позиция записана в документ, но склад
 * под неё ничего не откладывал. Так лежит необеспеченный заказ с площадки,
 * и продавец видит эту строку каждый раз, когда открывает такой заказ.
 */
export const DEAL_ITEM_STATUS_NAMES: Record<string, string> = {
  DRAFT: 'без резерва',
  // Предзаказ (задача 0170): деталь ещё не пришла, склад под неё ничего не
  // откладывал. Слово — «ожидается»: так владелец назвал это на экране.
  PREORDER: 'ожидается поставка',
  RESERVED: 'отложена',
  ISSUED: 'выдана',
  RETURNED: 'возвращена',
  CANCELLED: 'снята',
};

/**
 * То же состояние глазами покупателя, на странице по ссылке от продавца.
 *
 * <p>Слова другие намеренно: там речь о заказе покупателя, а не о документе
 * разборки, и сказано это безлично — «отложено», а не «отложена». Менять их
 * заодно со сведением копий нельзя: это то, что читает клиент.
 */
export const SHARED_DEAL_STATUS_NAMES: Record<string, string> = {
  DRAFT: 'оформляется',
  RESERVED: 'отложено',
  READY: 'готово к выдаче',
  ISSUED: 'выдано',
  CANCELLED: 'отменено',
  RETURNED: 'возвращено',
};

/**
 * Состояние сделки словом.
 *
 * <p>Незнакомое состояние возвращается как есть — сырым кодом. Это худший
 * из ответов, и он оставлен нарочно: выдуманное «Неизвестное состояние»
 * выглядело бы как настоящее слово и спрятало бы расхождение с сервером,
 * а сырой код виден и в отчёте о нём есть что искать. Полноту держит тест
 * против перечисления, а не эта строка.
 */
export function dealStatusName(status: string): string {
  return DEAL_STATUS_NAMES[status] ?? status;
}

/**
 * То же слово внутри предложения: «Сделка отложена — платить по ней не за что».
 *
 * <p>Отдельной таблицы для строчных слов нет и не будет: вторая таблица —
 * это вторая копия, а расходятся копии молча.
 */
export function dealStatusNameLower(status: string): string {
  return dealStatusName(status).toLowerCase();
}

/** Состояние позиции сделки словом. */
export function dealItemStatusName(status: string): string {
  return DEAL_ITEM_STATUS_NAMES[status] ?? status.toLowerCase();
}

/**
 * Стадия работы над сделкой — состоянием, которым её называют человеку.
 *
 * <p>Стадию считает сервер (`SalesService.stagesOf`, одно выражение `CASE`
 * на весь проект), а слов у неё своих нет: колонка доски называется «Готов
 * к выдаче», но сама сделка — «Готова к выдаче», и третий словарь рядом
 * с тремя существующими был бы четвёртой копией того же.
 *
 * <p>Названы здесь все пять стадий, а не одна сломанная: соответствие
 * «стадия — состояние, о котором она говорит» — это то, что экран обязан
 * знать про стадию, и написанное для одной вернуло бы ту же ошибку
 * в соседней. Возвращать её тихо: вёрстка на месте, слова неверные.
 */
const BOARD_STAGE_STATUS: Record<string, string> = {
  NEW: 'DRAFT',
  EXPIRED: 'RESERVED',
  AWAITING_PAYMENT: 'RESERVED',
  PARTLY_PAID: 'RESERVED',
  READY: 'READY',
};

/**
 * Чем подписывать сделку: стадией, если сервер её посчитал, иначе состоянием
 * документа.
 *
 * <p><b>Зачем эта поправка вообще.</b> У полностью оплаченной и не выданной
 * сделки документ так и остаётся `RESERVED` со сроком резерва: `READY`
 * не ставит ни один путь системы. Подписанная сырым статусом, она читается
 * как «Отложена до 15 сентября» — «ещё не оплачена, ждём до этой даты», —
 * то есть прямо противоположным смыслом, и продавец читает именно так.
 *
 * <p>Пустая стадия — это закрытая сделка (выдана, отменена, возвращена):
 * стадии у неё нет, и правильное слово как раз в состоянии документа.
 * Незнакомая (сервер завёл шестую стадию, экран о ней ещё не знает)
 * откатывается туда же: сырое состояние видно и объяснимо, выдуманное
 * слово — нет. Та же причина, по которой `dealStatusName` возвращает
 * незнакомый код как есть.
 *
 * <p>Возвращается **код состояния**, а не слово: тем же значением
 * подписывают сделку (`dealStatusName`) и считают срок резерва
 * (`reservationTerm`) — у готовой к выдаче его показывать не из чего,
 * и обе поверхности обязаны решать это одинаково.
 */
export function dealStageStatus(stage: string | null | undefined, status: string): string {
  if (stage === null || stage === undefined) {
    return status;
  }
  return BOARD_STAGE_STATUS[stage] ?? status;
}

/** Состояние заказа словом для покупателя. */
export function sharedDealStatusName(status: string): string {
  return SHARED_DEAL_STATUS_NAMES[status] ?? status;
}

/**
 * Как зовётся покупатель, которого продавец не проставил.
 *
 * <p><b>Решение владельца продукта от 12 сентября 2026</b>
 * (`tasks/0062-pustoy-klient-zovetsya-odinakovo.md`, раздел «Решение
 * владельца»), дословно: «Частное лицо по дефолту, если продавец сам
 * не проставил какого-то определённого клиента». То есть слово одно
 * на всю систему, и оно же — имя контрагента розничной продажи
 * (`shared/RetailCustomer.NAME` на сервере).
 *
 * <p><b>Чем это было до него.</b> Четырьмя словами об одном и том же:
 * реестр сделок и касса писали «Без клиента», реестр возвратов — «Частное
 * лицо», карточка сделки у продавца — «не указан», а история документа
 * на сервере — «без клиента» в кавычках. «Без клиента» читается как
 * «покупатель потерян» или «данные не заполнены», то есть как недоработка,
 * а «Частное лицо» — как обычная розничная продажа человеку с улицы;
 * владелец, сводящий кассу, видел в одном отчёте одно, в другом другое
 * и шёл искать разницу, которой нет. После задачи 0011 (продажа без
 * заведения клиента) таких строк стало заметно больше.
 *
 * <p><b>Почему здесь, а не двумя `??` на двух экранах.</b> По той же
 * причине, по которой сюда сведены слова состояний задачей 0048: копии
 * расходятся молча — и эти уже разошлись, на четыре написания.
 */
export const NO_CUSTOMER_NAME = 'Частное лицо';

/**
 * Имя клиента так, как его называют человеку: пусто — «Частное лицо».
 *
 * <p>Пустым считается только отсутствие имени (`null` от сервера,
 * `undefined` в старом ответе), но не пустая строка: у контрагента,
 * заведённого без имени, имя **есть кому** поправить, и звать его
 * «Частным лицом» значило бы утверждать, что покупателя не называли.
 */
export function customerName(name: string | null | undefined): string {
  return name ?? NO_CUSTOMER_NAME;
}
